import React, { useState, useEffect, useMemo, memo } from "react";
import { useParams } from "react-router-dom";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import { isEmpty, flatMap, uniq, filter } from "lodash";
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
} from "react-virtualized";
import { useSelector, useDispatch } from "react-redux";
import filterHandler from "../../../utils/filterHandler";
import useStableCallback from "../../../hooks/useStableCallback";
import CommonDialog from "../../../components/CommonDialog";
import FileExport from "../../../components/FileExport";
import PermissionsGate from "../../../permissions/PermissionsGate";
import { SCOPES } from "../../../permissions/permissions-map";
import CustomDrawer from "../../../components/CustomDrawer";
import CustomFloatingButton from "../../../components/CustomFloatingButton";
import { validPogItem } from "../../../utils/itemValidator";
import useRequest from "../../../hooks/useRequest";
import StoreSuppliesTotals from "../StoreSuppliesTotals";
import SSItemFilter from "../SSItemFilter";
import { useLazyGetSSTotalQuery } from "../../../services/ssTotalApi";
import StoreSuppliesListContainer from "../StoreSuppliesListContainer";
import StoreSuppliesBreadcrumbs from "../StoreSuppliesBreadcrumbs";
import CustomNoResult from "../../ManagePlanograms/CustomNoResult";
import {
  showNotifier,
  setGlobalLoader,
  setAdjQtyError,
  setPrevAdjQty,
} from "../../../store/globalSlice";
import {
  ENTITY_TYPES,
  KeepCacheAsIs,
  orderStatus,
  singleValue,
} from "../../../utils";
import { isItemDepartment } from "../../../utils/deptValidator";
import {
  saveUpdatedItems,
  prepareInitialData,
  checkItemsBeforeSave,
  prepareSettingsSaveData,
  postSettingsSaveUpdateData,
  checkNonReleasedDuplicateItems,
  getReleasedItemsWithPONumberMappings,
  prepareCreatePOData,
  postCreatePOUpdateData,
  postSaveDatesUpdateData,
  prepareSaveDatesData,
  prepareDeleteDatesData,
  postDeleteDatesUpdateData,
  prepareInlineSaveData,
  postInlineSaveUpdateData,
} from "./prepareData";
import SettingsForm from "../SettingsForm";
import SSItem from "../SSItem";
import { getItemCost } from "../../../utils/SSCalculation";
import configData from "../../../config.js";
import ColumnHeader from "../ColumnHeader";

export const CALCULATION_FIELDS = ["AdjQty"];
const OVERSCAN_ROW_COUNT = 10;
const itemCache = new CellMeasurerCache({
  fixedWidth: true,
  fixedHeight: true,
  defaultHeight: 65,
});

export const resetItemOnSelection = (item = {}) => {
  let updatedItem = { ...item };
  if (updatedItem?.SelectedItem === singleValue.yes) {
    if (updatedItem?.AdjQty > 0) {
      return updatedItem;
    }
    updatedItem = {
      ...updatedItem,
      AdjQty: updatedItem?.RecQty,
    };
    return { ...getItemCost(updatedItem) };
  }
  return { ...getItemCost(updatedItem, true) };
};

function StoreSuppliesItems() {
  const dispatch = useDispatch();
  const { pogId, departmentLetter } = useParams();
  const { requestMaker } = useRequest();
  const [data, setData] = useState([]);
  const [pogSummary, setPogSummary] = useState({});
  const [itemsLoading, setItemsLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  // selections are those which items user selected using checkbox, it contains item id only.
  const [selections, setSelections] = useState([]);
  // valid selections are those which shows valid items to be eligible for selection, it contains entire item object.
  const [validSelections, setValidSelections] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const user = useSelector((store) => store.global.user);
  const store = useSelector((store) => store.global.store);
  const itemFilters = useSelector((store) => store.global.itemFilters);
  const pogFilters = useSelector((store) => store.global.pogFilters);
  const adjQtyError = useSelector((store) => store.global.adjQtyError);

  const [getSSTotal] = useLazyGetSSTotalQuery();

  const isItemsDepartment = isItemDepartment({
    DepartmentLetter: departmentLetter,
  });

  const getPogSummary = useStableCallback(() => {
    const filterValues = {
      storeId: store.id,
      departmentLetter: departmentLetter,
      category: null,
      planogramValue: pogId,
      vendorName: null,
      skuNumber: null,
    };
    requestMaker({
      method: "post",
      url: configData.GetSSPogList,
      data: filterValues,
    }).then((data) => {
      const fetchedData = data?.data || [];
      setPogSummary(fetchedData[0] || {});
    });
  });

  useEffect(() => {
    getPogSummary();

    requestMaker({
      method: "post",
      url: configData.GetSSItemList,
      data: {
        storeId: store.id,
        departmentLetter,
        planogramNumber: pogId,
        vendorName: pogFilters?._vendor,
        skuNumber: pogFilters?._sku,
      },
    })
      .then((data) => {
        const fetchedData = flatMap(data.data) || [];
        const itemList = prepareInitialData(fetchedData);
        const updatedItems = filterHandler(
          itemFilters,
          itemList,
          itemList,
          ENTITY_TYPES.ssItem
        );
        setValidSelections(updatedItems.filter((item) => validPogItem(item)));
        setFilteredData(updatedItems);
        setData(itemList);
        setOriginalData(itemList);
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        setItemsLoading(false);
      });

    // eslint-disable-next-line
  }, []);

  const pogFreezed = pogSummary?.ReleaseStatus === orderStatus.released;
  const pogAvailable = pogSummary?.Selected === singleValue.yes;

  const isRowLoaded = ({ index }) =>
    index < filteredData.length && !filteredData[index]?.virtualLoading;

  const loadMoreRows = ({ startIndex, stopIndex }) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const d = [...filteredData];
        for (let idx = startIndex; idx <= stopIndex; idx++) {
          d[idx].virtualLoading = false;
        }
        setFilteredData(d);
        resolve();
      }, 800);
    });
  };

  const toggleDrawer = useStableCallback((open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setIsDrawerOpen(open);
  });

  const handleItemReset = useStableCallback(() => {
    setShowResetDialog(true);
  });

  const handleResetDialogClose = useStableCallback(() => {
    setShowResetDialog(false);
  });

  const handleResetDialogDone = useStableCallback(() => {
    const updatedData = filterHandler(
      itemFilters,
      originalData,
      originalData,
      ENTITY_TYPES.ssItem
    );
    const validItems = updatedData.filter((item) => validPogItem(item));
    setValidSelections(validItems);
    setFilteredData(() => KeepCacheAsIs(filteredData, updatedData));
    setData(() => originalData);
    setSelectAll(false);
    setSelections([]);
    if (adjQtyError) {
      dispatch(setAdjQtyError(null));
      dispatch(setPrevAdjQty(null));
    }
    handleResetDialogClose();
    window.scrollTo(0, 0);
  });

  const handleSelectAll = useStableCallback((event) => {
    const checked = event?.target?.checked;
    setFilteredData((oldData) => {
      return oldData?.map((item) => {
        if (validPogItem(item)) {
          checked && setSelections((oldSel) => uniq([...oldSel, item?._id]));
          return {
            ...item,
            selection: checked,
          };
        }
        return item;
      });
    });

    setData((oldData) => {
      return oldData?.map((item) => {
        // Only filtered item set selection value.
        const itemFound = filteredData.find((fi) => fi?._id === item?._id);
        if (itemFound && validPogItem(item)) {
          checked && setSelections((oldSel) => uniq([...oldSel, item?._id]));
          return {
            ...item,
            selection: checked,
          };
        }
        return item;
      });
    });

    if (!checked) {
      setSelections([]);
    }
    setSelectAll(!selectAll);
  });

  const handleFilterSubmit = useStableCallback(() => {
    const updatedData = filterHandler(
      itemFilters,
      data,
      originalData,
      ENTITY_TYPES.ssItem
    );
    const validItems = updatedData.filter((item) => validPogItem(item));
    itemCache.clearAll();
    setValidSelections(validItems);
    setFilteredData(updatedData);

    const selectionCount = updatedData.filter((i) => !!i?.selection);
    if (
      selectionCount.length === selections.length &&
      selections.length === updatedData.length &&
      selections.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  });

  const handleFilterReset = useStableCallback(() => {
    const updatedData = filterHandler(
      itemFilters,
      data,
      originalData,
      ENTITY_TYPES.ssItem
    );
    const validItems = updatedData.filter((item) => validPogItem(item));
    itemCache.clearAll();
    setValidSelections(validItems);
    setFilteredData(updatedData);
    if (selectAll) {
      const selectionCount = updatedData.filter((i) => i?.selection);
      if (
        selectionCount.length === selections.length &&
        selections.length === updatedData.length &&
        selections.length
      ) {
        setSelectAll(true);
      } else {
        setSelectAll(false);
      }
    }
  });

  const handleFilterSort = useStableCallback(() => {
    const updatedData = filterHandler(
      itemFilters,
      filteredData,
      originalData,
      ENTITY_TYPES.ssItem
    );
    const validItems = updatedData.filter((item) => validPogItem(item));
    setValidSelections(validItems);
    setFilteredData(KeepCacheAsIs(filteredData, updatedData));
  });

  const handleProcessData = useStableCallback((id) => ({ field, value }) => {
    const getUpdatedData = (oldItems) => {
      const updatedData = [...oldItems];
      let index = oldItems.findIndex((i) => i?._id === id);
      let item = oldItems[index];
      item = { ...item, [field]: value };
      if (field === "SelectedItem") {
        item = {
          ...resetItemOnSelection(item),
        };
      }
      item = CALCULATION_FIELDS.includes(field) ? getItemCost(item) : item;
      updatedData[index] = { ...item };
      return updatedData;
    };
    setFilteredData(getUpdatedData);
    setData(getUpdatedData);

    // Handles only Item to Select All auto selection functionality.
    if (field === "selection") {
      if (value) {
        setSelections((oldVal) => {
          const updatedSelections = uniq([...oldVal, id]);
          setSelectAll(updatedSelections.length === validSelections.length);
          return updatedSelections;
        });
      } else {
        setSelections((oldVal) => {
          return filter(oldVal, (itemId) => itemId !== id);
        });
        setSelectAll(false);
      }
    }
  });

  const handleInlineItemSave = useStableCallback((item) => {
    const updatedData = prepareInlineSaveData(
      item,
      store.id,
      user.email,
      isItemsDepartment
    );
    dispatch(setGlobalLoader(true));

    requestMaker({
      method: "post",
      url: configData.SSItemSave,
      data: updatedData?.itemList || [],
    })
      .then(() => {
        const postSaveUpdatedItems = postInlineSaveUpdateData(
          item,
          filteredData
        );
        setData(saveUpdatedItems(postSaveUpdatedItems));
        setOriginalData(saveUpdatedItems(postSaveUpdatedItems));
        setFilteredData((prevState) => {
          const getUpdates = saveUpdatedItems(postSaveUpdatedItems);
          const _partialUpdates = getUpdates(prevState);
          const updatedData = filterHandler(
            itemFilters,
            _partialUpdates,
            originalData,
            ENTITY_TYPES.ssItem
          );
          return updatedData;
        });
        setSelectAll(false);
        setSelections([]);
        dispatch(showNotifier({ message: "Item details saved successfully" }));
        getSSTotal({ storeId: store?.id, type: "" });
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const handleSettingsSave = (selected) => {
    const isValidSaveData = checkItemsBeforeSave(
      dispatch,
      showNotifier,
      selected,
      filteredData,
      selections
    );

    if (!isValidSaveData) {
      return false;
    }

    const updatedData = prepareSettingsSaveData(
      selected,
      store.id,
      user?.email,
      filteredData,
      selections
    );
    dispatch(setGlobalLoader(true));

    requestMaker({
      method: "post",
      url: configData.SSItemSave,
      data: updatedData?.itemList,
    })
      .then(() => {
        const postSaveUpdatedItems = postSettingsSaveUpdateData(
          selected,
          filteredData,
          selections
        );
        setData(saveUpdatedItems(postSaveUpdatedItems));
        setOriginalData(saveUpdatedItems(postSaveUpdatedItems));
        setFilteredData((prevState) => {
          const getUpdates = saveUpdatedItems(postSaveUpdatedItems);
          const _partialUpdates = getUpdates(prevState);
          const updatedData = filterHandler(
            itemFilters,
            _partialUpdates,
            originalData,
            ENTITY_TYPES.ssItem
          );
          return updatedData;
        });
        setSelectAll(false);
        setSelections([]);
        dispatch(showNotifier({ message: "Item details saved successfully" }));
        getSSTotal({ storeId: store?.id, type: "" });
      })
      .catch(() => {
        dispatch(
          showNotifier({
            message: "Something went wrong. Please try again.",
            severity: "error",
          })
        );
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  };

  const onCreatePONumbers = (details) => {
    if (isItemsDepartment) {
      const isValidItemSelection = checkNonReleasedDuplicateItems(
        details,
        getReleasedItemsWithPONumberMappings(filteredData),
        filteredData,
        selections
      );
      if (!isValidItemSelection) {
        dispatch(
          showNotifier({
            message:
              "Can not generate PO number for selected items, since same PO number is not allowed for multiple items having same item number.",
            severity: "error",
          })
        );
        return false;
      }
    }
    const isValidSaveData = checkItemsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareCreatePOData(
      {
        departmentLetter,
        pogId,
      },
      store.id,
      details,
      user?.email,
      selections,
      filteredData,
      isItemsDepartment
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.SSItemCreatePO,
      data: updatedData,
    })
      .then((data) => {
        const postDeleteUpdatedItems = postCreatePOUpdateData(
          filteredData,
          data?.data,
          selections
        );
        setFilteredData(saveUpdatedItems(postDeleteUpdatedItems, true));
        setData(saveUpdatedItems(postDeleteUpdatedItems, true));
        setOriginalData(saveUpdatedItems(postDeleteUpdatedItems, true));
        dispatch(
          showNotifier({
            message: "Saved changes to PO number type.",
          })
        );
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not generate PO number for selected items.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
      });
  };

  const checkPOGenerated = useStableCallback(() => {
    let isAllPOGenerated = true;
    const poNumbers = filteredData
      ?.map((i) => {
        if (selections.includes(i?._id) && !!i?.PONumber) {
          return i._id;
        }
        return undefined;
      })
      ?.filter((i) => !!i);
    if (poNumbers.length === selections.length) {
      isAllPOGenerated = true;
    } else {
      isAllPOGenerated = false;
    }
    return isAllPOGenerated;
  });

  const onSaveDates = useStableCallback((releaseDate) => {
    const isValidSaveData = checkItemsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    if (!checkPOGenerated()) {
      return dispatch(
        showNotifier({
          message:
            "PO Number for some selected items are missing. Please generate PO Number.",
          severity: "error",
        })
      );
    }
    const updatedData = prepareSaveDatesData(
      {
        departmentLetter,
        pogId,
      },
      releaseDate,
      store.id,
      user?.email,
      selections,
      filteredData,
      isItemsDepartment
    );
    dispatch(setGlobalLoader(true));

    requestMaker({
      method: "post",
      url: configData.SSItemSaveDate,
      data: updatedData,
    })
      .then(() => {
        const postSaveUpdatedItems = postSaveDatesUpdateData(
          releaseDate,
          filteredData,
          selections
        );
        setFilteredData(saveUpdatedItems(postSaveUpdatedItems));
        setData(saveUpdatedItems(postSaveUpdatedItems));
        setOriginalData(saveUpdatedItems(postSaveUpdatedItems));
        setSelectAll(false);
        setSelections([]);
        dispatch(
          showNotifier({ message: "Release Dates saved successfully." })
        );
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not save the date for selected items.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const onDeleteDates = useStableCallback(() => {
    const isValidSaveData = checkItemsBeforeSave(
      dispatch,
      showNotifier,
      false,
      filteredData,
      selections,
      true
    );
    if (!isValidSaveData) {
      return false;
    }
    const updatedData = prepareDeleteDatesData(
      {
        departmentLetter,
        pogId,
      },
      store.id,
      user.email,
      selections,
      filteredData,
      isItemsDepartment
    );
    dispatch(setGlobalLoader(true));
    requestMaker({
      method: "post",
      url: configData.SSItemDeleteDate,
      data: updatedData,
    })
      .then(() => {
        const postDeleteUpdatedItems = postDeleteDatesUpdateData(
          filteredData,
          selections
        );
        setFilteredData(saveUpdatedItems(postDeleteUpdatedItems));
        setData(saveUpdatedItems(postDeleteUpdatedItems));
        setOriginalData(saveUpdatedItems(postDeleteUpdatedItems));
        setSelectAll(false);
        setSelections([]);
        dispatch(
          showNotifier({ message: "Release Dates deleted successfully." })
        );
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          dispatch(
            showNotifier({
              message: "Can not delete the date for selected items.",
              severity: "error",
            })
          );
        } else {
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          );
        }
      })
      .finally(() => {
        dispatch(setGlobalLoader(false));
        setIsDrawerOpen(false);
      });
  });

  const listRenderer = ({ index, key, style, parent }) => {
    const item = filteredData[index] || {};
    return (
      <CellMeasurer
        key={key}
        cache={itemCache}
        parent={parent}
        columnIndex={0}
        rowIndex={index}
      >
        {({ registerChild, _measure }) => (
          <div style={style} key={key} ref={registerChild}>
            <SSItem
              key={item?._id}
              item={item}
              handleProcessData={handleProcessData(item?._id)}
              handleInlineSave={handleInlineItemSave}
              pogFreezed={pogFreezed}
              pogAvailable={pogAvailable}
            />
          </div>
        )}
      </CellMeasurer>
    );
  };

  const hideSettings = useMemo(() => {
    const isSelectedItemFound = filteredData.find((i) =>
      selections.includes(i?._id)
    );
    return isEmpty(selections) || Boolean(adjQtyError) || !isSelectedItemFound;
  }, [selections, filteredData, adjQtyError]);

  return (
    <Container id="page-container" maxWidth={false} sx={{ paddingY: 1 }}>
      <Stack spacing={1}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <StoreSuppliesBreadcrumbs pogName={pogSummary?.PlanogramName} />
        </div>
        <StoreSuppliesTotals />
        <SSItemFilter
          disableFilters={adjQtyError}
          handleFilter={handleFilterSubmit}
          handleReset={handleFilterReset}
          handleSortChange={handleFilterSort}
        />
        <StoreSuppliesListContainer
          isResultPresent={!!filteredData.length}
          resultLoading={itemsLoading}
          stickyHeader={<ColumnHeader entity={ENTITY_TYPES.item} />}
          handleSelectAll={handleSelectAll}
          selectAll={validSelections.length ? selectAll : false}
          entityType={ENTITY_TYPES.ssItem}
          hideSelectAll={pogFreezed}
          disableSelectAll={!pogAvailable || pogFreezed}
          exportButton={<FileExport data={filteredData} type="ssItem" />}
          resetAction={
            <PermissionsGate scopes={[SCOPES.canEdit]}>
              {!itemsLoading && !!filteredData.length && (
                <>
                  <Button
                    variant="outlined"
                    sx={{ alignSelf: "flex-end" }}
                    onClick={handleItemReset}
                    size="small"
                    disabled={
                      store?.pending ||
                      store?.isArchived ||
                      store?.isCompleted ||
                      store?.dataguideInProgress
                    }
                  >
                    Reset
                  </Button>
                  <CommonDialog
                    open={showResetDialog}
                    title="Reset All Unsaved Items"
                    content={
                      "Are you sure, you want to reset/revert all unsaved item changes?"
                    }
                    type="yesNo"
                    handleClose={handleResetDialogClose}
                    handleDone={handleResetDialogDone}
                  />
                </>
              )}
            </PermissionsGate>
          }
        >
          {filteredData.length ? (
            <div className="_virtual-container">
              <div className="_virtual-list-wrapper">
                <InfiniteLoader
                  isRowLoaded={isRowLoaded}
                  loadMoreRows={loadMoreRows}
                  rowCount={filteredData.length}
                >
                  {({ onRowsRendered, registerChild }) => (
                    <AutoSizer>
                      {({ width, height }) => (
                        <List
                          width={width}
                          height={height}
                          ref={registerChild}
                          deferredMeasurementCache={itemCache}
                          rowHeight={itemCache.rowHeight}
                          rowRenderer={listRenderer}
                          onRowsRendered={onRowsRendered}
                          rowCount={filteredData.length}
                          overscanRowCount={OVERSCAN_ROW_COUNT}
                        />
                      )}
                    </AutoSizer>
                  )}
                </InfiniteLoader>
              </div>
            </div>
          ) : (
            <CustomNoResult entity={ENTITY_TYPES.item} loading={itemsLoading} />
          )}
        </StoreSuppliesListContainer>
      </Stack>
      {!hideSettings && <CustomFloatingButton onClick={toggleDrawer(true)} />}
      <CustomDrawer open={isDrawerOpen} onClose={toggleDrawer(false)}>
        <SettingsForm
          onClose={toggleDrawer(false)}
          onSettingsSave={handleSettingsSave}
          onCreatePONumbers={onCreatePONumbers}
          onSaveDates={onSaveDates}
          onDeleteDates={onDeleteDates}
          isXDepartment={isItemsDepartment}
        />
      </CustomDrawer>
    </Container>
  );
}

export default memo(StoreSuppliesItems);
